import * as reduxConstants from "@/state/reduxConstants";
import {Action} from "redux";
import {ErrorMessageContent} from "@/util/errorUtil";
import {actionCreator, AppThunkAction} from "@/state/actions";
import hj from "@/tracking/hotjar";
import {
    IClientConcept,
    IUserInfo,
    IUserSettings,
    IBusinessUnit,
    IClientConceptMetricDefinition,
    IPeriodStartDate,
    IClientConceptPage,
    IProblemDetails
} from "../../application/api/applicationModels";
import authApi from "../api/authAPI";

// Define action interfaces
interface IAuthSuccessAction extends Action<typeof reduxConstants.AUTH_SUCCESS> {
    clientConcepts: IClientConcept[];
    clientConceptsForCoachDetect: IClientConcept[];
    clientConceptsForCoachHomeOffice: IClientConcept[];
    clientConceptsForCoachDeliveryPage: IClientConcept[];
    clientConceptsForCoachDifferentialPay: IClientConcept[];
    clientConceptsForCoachRecovery: IClientConcept[];
    clientConceptsForDeliveryOperations: IClientConcept[];
    clientTreeHierarchy: IBusinessUnit[];
    clientConceptMetricDefinitions: IClientConceptMetricDefinition[];
    clientConceptPages: IClientConceptPage[];
    userInfo: IUserInfo;
    isClientOptedOutOfTaskEmailing: boolean;
    openTaskCount: number;
    userSettings: IUserSettings;
    errorMessage: string;
    periodStartDates: IPeriodStartDate[];
    bannerMessage: string;
}

interface IAuthenticationFailureAction
    extends Action<typeof reduxConstants.AUTH_FAILURE> {
    error: string;
}

interface IAuthorizationFailureAction
    extends Action<typeof reduxConstants.AUTHORIZATION_FAILURE> {
    error: string;
}

interface IAuthErrorAction extends Action<typeof reduxConstants.AUTH_ERROR> {
    error: string;
}

 
interface IAuthSignOutAction extends Action<typeof reduxConstants.AUTH_SIGNOUT> {}

export const authSuccess = actionCreator<IAuthSuccessAction>(
    reduxConstants.AUTH_SUCCESS
);
export const authenticationFailure = actionCreator<IAuthenticationFailureAction>(
    reduxConstants.AUTH_FAILURE
);
export const authorizationFailure = actionCreator<IAuthorizationFailureAction>(
    reduxConstants.AUTHORIZATION_FAILURE
);
export const authError = actionCreator<IAuthErrorAction>(reduxConstants.AUTH_ERROR);
export const signOut = actionCreator<IAuthSignOutAction>(
    reduxConstants.AUTH_SIGNOUT
);
export const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) return error.message;
    return String(error);
};

export type AuthAction =
    | IAuthSuccessAction
    | IAuthenticationFailureAction
    | IAuthorizationFailureAction
    | IAuthErrorAction
    | IAuthSignOutAction;

// Define thunk action creators
export const actionCreators = {
    authenticateFailed(error: unknown): AppThunkAction<AuthAction> {
        return (dispatch) => {
            dispatch(authenticationFailure({error: getErrorMessage(error)}));
        };
    },

    // Demo auth: check the Identity cookie via GET /api/account/me and hydrate Redux state.
    authenticateViaIdentity(): AppThunkAction<AuthAction> {
        return (dispatch) => {
            authApi
                .authenticateViaIdentity()
                .then((data) => {
                    dispatch(
                        authSuccess({
                            clientConcepts: data.clientConcepts,
                            clientConceptsForCoachDetect: data.clientConceptsForCoachDetect,
                            clientConceptsForCoachHomeOffice: data.clientConceptsForCoachHomeOffice,
                            clientConceptsForCoachDeliveryPage: data.clientConceptsForCoachDeliveryPage,
                            clientConceptsForCoachDifferentialPay: data.clientConceptsForCoachDifferentialPay,
                            clientConceptsForCoachRecovery: data.clientConceptsForCoachRecovery,
                            clientConceptsForDeliveryOperations: data.clientConceptsForDeliveryOperations,
                            clientTreeHierarchy: data.clientTreeHierarchy,
                            clientConceptMetricDefinitions: data.clientConceptMetricDefinitions,
                            clientConceptPages: data.clientConceptPages,
                            userInfo: data.userInfo,
                            isClientOptedOutOfTaskEmailing: data.isClientOptedOutOfTaskEmailing,
                            openTaskCount: data.openTaskCount,
                            userSettings: data.userSettings,
                            errorMessage: data.errorMessage,
                            periodStartDates: data.periodStartDates,
                            bannerMessage: data.bannerMessage
                        })
                    );
                    hj("identify", data.userInfo.clientUserID, {
                        ClientCode: data.userInfo.clientCode,
                        UserName: data.userInfo.userName
                    });
                })
                .catch((res: IProblemDetails) => {
                    // 401 is already dispatched as authenticationFailure by apiUtil validateResponse.
                    if (res.status === 401) return;
                    if (res.status === 403) {
                        dispatch(
                            authorizationFailure({
                                error: res.detail || res.title || ErrorMessageContent.NoDashboardAccess
                            })
                        );
                    } else {
                        dispatch(
                            authError({
                                error: res.detail || res.title || ErrorMessageContent.SomethingWentWrong
                            })
                        );
                    }
                });
        };
    },

    signOut(): AppThunkAction<AuthAction> {
        return (dispatch) => {
            dispatch(signOut({}));
        };
    }
};
